import React, { useCallback, useMemo, useState, useEffect } from 'react';
import pt from 'prop-types';
import currency from 'currency.js';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { capitalize } from '@material-ui/core';
import styles from './ReviewModal.module.scss';
import Modal from './Modal';
import { SheetPropType } from './PropTypes';
import DisplayableSheet from './DisplayableSheet';
import penIcon from '../../../assets/pen.png';
import warningIcon from '../../../assets/warning.png';
import LoadingOverlay from './LoadingOverlay';
import cn from 'classnames';
import Builder from './Builder';

export default function ReviewModal({ onCancel = () => {}, poses = [], backgrounds = [], cancelText = 'Cancel', additionalPoses, isLoading, errors, option, onConfirm, sheets, setIndex }) {
  const [editableSheetIndex, setEditableSheetIndex] = useState(-1);
  const [editableSheets, setEditableSheets] = useState(sheets);

  const handleConfirm = useCallback(() => onConfirm(editableSheets, additionalPoses), [editableSheets, additionalPoses]);
  const handleCancel = useCallback(() => onCancel(editableSheets, additionalPoses, true), [editableSheets, additionalPoses]);

  const handleSheetEdit = (sheets) => {
    const newSheets = editableSheets.map((sheet, i) => {
      if (i !== editableSheetIndex) return sheet;

      return sheets[0];
    });

    setEditableSheets(newSheets);
    setEditableSheetIndex(-1);
    document.querySelectorAll('.ReactModalPortal').forEach((node) => node.style.display = 'block');
  };

  const startEdit = (i) => {
    document.querySelectorAll('.ReactModalPortal').forEach((node) => node.style.display = 'none');
    setEditableSheetIndex(i);
  };

  useEffect(() => {
    setEditableSheets(sheets);
  }, [sheets]);

  return (
    <>
      {sheets[editableSheetIndex] && (
        <div className={styles.singleSheetEditor}>
          <Builder
            option={option}
            backgrounds={backgrounds}
            favoriteIds={[]}
            imageTypes={[sheets[editableSheetIndex].imageType]}
            initialSheets={[sheets[editableSheetIndex]]}
            poses={poses}
            inReview
            onConfirm={handleSheetEdit}
          />
        </div>
      )}
      <Modal>
        <Modal.Trigger>Review</Modal.Trigger>
        <Modal.Content className={cn(styles.content, 'reviewModal')}>
          <div style={{ position: 'relative' }}>
            {isLoading && <LoadingOverlay />}
            <div>
              <div className={styles.headerWrapper}>
                <Modal.Title className={styles.title}>Review Your Package</Modal.Title>
                <Modal.Close className={styles.close}>X</Modal.Close>
              </div>
              {Object.keys(errors).length > 0 && (
                <div className={styles.errorSection}>
                  <img src={warningIcon} alt="Warning" />
                  <p>
                    There was a problem generating your package.
                    Please refresh the page and try again, come back at a later date or call
                    {' '}
                    <a className={styles.link} href="tel:209-567-0748">(209) 567-0748</a>
                    {' '}
                    for assistance.
                  </p>
                </div>
              )}
              {Object.keys(errors).length === 0 && (
                <div>
                  <p className={styles.optionName}>{option.name}</p>
                  <div>
                    {editableSheets.map((sheet, i) => (
                      <div key={i} className={styles.sheetsWrapper}>
                        <div className={styles.sheetsHeader}>
                          <div>{capitalize(sheet?.imageType?.name?.toLowerCase())}</div>
                          <button type="button" className={styles.editButton} onClick={() => startEdit(i)}>
                            <img alt="Edit" src={penIcon} />
                            <div>Edit</div>
                          </button>
                        </div>
                        <div className={styles.displayableSheets}>
                          <DisplayableSheet className={styles.displayableSheet} sheet={sheet} />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={styles.subtotal}>
                    {editableSheets.length > 0 && (
                      <span>
                      {`Subtotal: 
                       ${currency(editableSheets.map(sheet => sheet.addon.priceWithout).reduce((total, sheet) => total + sheet)).format()}`}
                    </span>
                    )}
                  </div>
                  <div className={styles.footer}>
                    <button onClick={handleConfirm} type="button">Continue to Checkout</button>
                    <Modal.Close onClick={handleCancel}>{cancelText}</Modal.Close>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Modal.Content>
      </Modal>
    </>
  );
}

ReviewModal.defaultProps = {
  additionalPoses: 0,
  isLoading: false,
  errors: {},
};

ReviewModal.propTypes = {
  additionalPoses: pt.number,
  option: pt.shape({
    additionalPoseCharge: pt.number.isRequired,
    id: pt.string.isRequired,
    name: pt.string.isRequired,
    prices: pt.arrayOf(pt.shape({
      price: pt.number.isRequired,
    })),
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  errors: pt.object,
  onConfirm: pt.func.isRequired,
  isLoading: pt.bool,
  sheets: pt.arrayOf(SheetPropType).isRequired,
  setIndex: pt.func.isRequired,
};
